import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

interface PageTransitionProps {
  children: ReactNode;
}

const PageTransition = ({ children }: PageTransitionProps) => {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [stage, setStage] = useState<'visible' | 'fadeOut' | 'fadeIn'>('visible');
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;

    setStage('fadeOut');

    const fadeOutTimer = setTimeout(() => {
      setDisplayChildren(children);
      setStage('fadeIn');

      const fadeInTimer = setTimeout(() => {
        setStage('visible');
      }, 220);

      return () => clearTimeout(fadeInTimer);
    }, 180);

    return () => clearTimeout(fadeOutTimer);
  }, [location.pathname, children]);

  const opacity =
    stage === 'visible' ? 1 :
    stage === 'fadeOut' ? 0 :
    0;

  const translateY =
    stage === 'visible' ? 0 :
    stage === 'fadeOut' ? -6 :
    8;

  return (
    <div
      style={{
        opacity,
        transform: `translateY(${translateY}px)`,
        transition:
          stage === 'fadeOut'
            ? 'opacity 180ms ease-in, transform 180ms ease-in'
            : stage === 'fadeIn'
            ? 'opacity 220ms ease-out, transform 220ms ease-out'
            : 'none',
        willChange: 'opacity, transform',
      }}
    >
      {displayChildren}
    </div>
  );
};

export default PageTransition;
